import PropTypes from 'prop-types';
import React, { Component } from 'react';
import moment from 'moment-timezone';
import findIndex from 'lodash/findIndex';
import get from 'lodash/get';
import clamp from 'lodash/clamp';
import classNames from 'classnames';

import config from '../../../../../config';
import Icon, { GLYPHS } from '../../../../common/icon';
import * as propTypes from '../propTypes';

class SelectTime extends Component {
  constructor(props) {
    super(props);

    this.state = {
      transformLeft: 0,
      scrolled: false,
      clicked: false,
      unavailableError: false,
    };

    this.step = 0;
    this.itemWidth = 0;
    this.containerWidth = 0;
    this.wrapperWidth = 0;
  }

  componentWillReceiveProps() {
    this.setState({
      clicked: false,
      scrolled: false,
    });
  }

  componentDidUpdate(prevProps) {
    if (this.props.expanded && (!prevProps.expanded || !prevProps.dateTime)) {
      this.prepareCarousel();
    }

    if (this.props.unavailable) {
      this.unavailableError();
    } else {
      this.hideUnavailableError();
    }
  }

  onTimeClick = time => () => {
    this.setState({
      clicked: true,
    }, this.props.setTime(time));
  };

  leftScroll = () => {
    let value = this.state.transformLeft;
    if (this.containerWidth > this.wrapperWidth) {
      value += this.step;
      if (value > 0) {
        value = 0;
      }
    }
    this.setState({
      transformLeft: value,
      scrolled: true,
    });
  };

  rightScroll = () => {
    let value = this.state.transformLeft;
    if (this.containerWidth > this.wrapperWidth) {
      value -= this.step;
      if (value < this.wrapperWidth - this.containerWidth) {
        value = this.wrapperWidth - this.containerWidth;
      }
    }
    this.setState({
      transformLeft: value,
      scrolled: true,
    });
  };

  scrollToSelectedTime() {
    const {
      scrolled,
      clicked,
      transformLeft,
    } = this.state;
    const {
      times,
      day,
      edit,
      timezone,
    } = this.props;
    const firstWorkTimeIndex = findIndex(times, 'work');
    const firstTimeAfterNow = findIndex(times, time => time.time > moment.tz(timezone).format('HH:mm'));

    const shouldScroll = (this.props.time || firstWorkTimeIndex !== -1 || firstTimeAfterNow !== -1) && !scrolled && !clicked;
    if (!shouldScroll) return;

    const calculateOffset = () => {
      let result;

      if (this.props.time) {
        const timePos = -findIndex(times, ['time', this.props.time]) * this.itemWidth;
        result = timePos + (this.wrapperWidth / 2);
      } else {
        const today = moment.tz(timezone).format(config.dateFormatISO);
        const todayIsSelectedDay = moment(day, config.dateFormatISO).isSame(today, 'day');

        const index = (!edit && todayIsSelectedDay && firstTimeAfterNow > firstWorkTimeIndex)
          ? firstTimeAfterNow : firstWorkTimeIndex;

        result = -index * this.itemWidth;
      }
      return clamp(result, this.wrapperWidth - this.containerWidth, 0);
    };

    if (calculateOffset() !== transformLeft) this.setState({ transformLeft: calculateOffset(), scrolled: true });
  }

  getItemsCount() { return this.container && this.container.children.length; }

  setItemWidth() {
    // the number 61 is the width (53) + horizontal margin 4 on both sides
    // from the .c-appointment-details-time-item from scss
    const DEFAULT_ITEM_WIDTH = 61;
    const child = get(this.container, 'children[0]');
    if (child) {
      const style = child.currentStyle || window.getComputedStyle(child);
      const width = child.offsetWidth + parseFloat(style.marginLeft) + parseFloat(style.marginRight);
      this.itemWidth = width;
      return;
    }
    this.itemWidth = DEFAULT_ITEM_WIDTH;
  }

  setContainerWidth() {
    this.containerWidth = this.getItemsCount() * this.itemWidth;
    if (this.containerWidth > 0) {
      this.container.style.width = `${this.containerWidth}px`;
    }
  }

  setCarouselWidths() {
    if (!this.getItemsCount()) return;

    const DEFAULT_WRAPPER_WIDTH = 584;
    this.setItemWidth();
    this.setContainerWidth();
    this.wrapperWidth = this.wrapper.offsetWidth || DEFAULT_WRAPPER_WIDTH;
    this.step = this.itemWidth * 5;
  }

  hideUnavailableError() {
    if (this.state.unavailableError) this.setState({ unavailableError: false });
  }

  unavailableError() {
    if (this.state.unavailableError) return;
    this.setState(
      { unavailableError: true },
      () => this.props.setTime(null)
    );
  }

  prepareCarousel = () => {
    this.setCarouselWidths();
    this.scrollToSelectedTime();
  };

  renderTimes() {
    const { times, loading } = this.props;

    return times.map((item) => {
      const isSelected = item.time === this.props.time;
      const className = classNames(
        'c-appointment-details-time-item',
        { 'c-appointment-details-time-item--out': !isSelected && !item.available,
          'c-appointment-details-time-item--selected': isSelected,
          'is-disabled': loading }
      );
      const time = moment(item.time, 'HH:mm');
      const child = isSelected ? (
        <b>{ time.format('a') }</b>
      ) : (
        <span className="c-heading--lightgrey">{ time.format('a') }</span>
      );

      return (
        <button
          data-automation-id={`button:time:${item.time}${(!isSelected && !item.available) ? ':out' : ''}`}
          key={item.time}
          onClick={this.onTimeClick(item.time)}
          className={className}
          type="button"
        >
          { time.format('h:mm') }<br />
          { child }
        </button>
      );
    });
  }

  render() {
    const { transformLeft } = this.state;
    const { errorMessage } = this.props;
    const isLoading = !errorMessage && !this.props.times.length && this.props.loading;

    if (isLoading) {
      return (
        <div className="layout layout--flush" />
      );
    }

    if (errorMessage) {
      return (
        <div className="layout layout--flush">
          <div className="layout__item u-text-center u-fontsize-extrasmall u-font-color-grey">
            { errorMessage }
          </div>
        </div>
      );
    }

    const containerStyle = {
      transform: `translate(${transformLeft}px, 0)`,
    };
    const arrowClass = 'u-inline-block u-1/12 u-text-center c-heading--lightgrey u-align-middle c-appointment-details-time-arrow';

    return (
      <div
        className="layout--flush u-mt-"
        display-if={this.props.expanded}
      >
        <div className={arrowClass}>
          <Icon
            glyph={GLYPHS.arrowLeft}
            className="c-icon"
            onClick={this.leftScroll}
            data-automation-id="button:scrollLeft"
          />
        </div>
        <div
          className="layout__item u-10/12 c-appointment-details-time-wrapper"
          ref={(ref) => { this.wrapper = ref; }}
        >
          <div
            className="c-appointment-details-time-container"
            style={containerStyle}
            ref={(ref) => { this.container = ref; }}
          >
            { this.renderTimes() }
          </div>
          <div
            className="c-label c-label--error u-text-center"
            display-if={this.state.unavailableError}
          >
            Selected time is not available.<br />
          </div>
        </div>
        <div className={arrowClass}>
          <Icon
            glyph={GLYPHS.arrowRight}
            className="c-icon"
            onClick={this.rightScroll}
            data-automation-id="button:scrollRight"
          />
        </div>
      </div>
    );
  }
}

SelectTime.propTypes = {
  timezone: PropTypes.string,
  day: PropTypes.string,
  time: PropTypes.string,
  setTime: PropTypes.func,
  edit: PropTypes.bool,
  errorMessage: PropTypes.string,
  loading: PropTypes.bool,
  times: propTypes.times,
  unavailable: PropTypes.bool,
  expanded: PropTypes.bool,
};

export default SelectTime;
